// D-KEFS Color-Word Interference (Delis et al., 2001) — parquet lookup.
// Tables D.1-D.4: raw time → scaled score
// Tables D.7-D.10, D.12-D.13: raw errors → cumulative % (≥)
// Tables D.11, D.14: raw total errors → scaled score

import { makeParquetQuerier } from "./parquetQuery";
import { matchesRange } from "./rangeMatch";

const queryParquet = makeParquetQuerier("Stroop-DKEFS");

// Patient age → parquet column name (matches scripts/create_stroop_dkefs_parquets.py).
function getAgeColumn(age) {
  const a = Math.floor(age);
  if (a >= 8 && a <= 15) return `age_${a}`;
  if (a >= 16 && a <= 19) return "age_16_19";
  if (a >= 20 && a <= 29) return "age_20_29";
  if (a >= 30 && a <= 39) return "age_30_39";
  if (a >= 40 && a <= 49) return "age_40_49";
  if (a >= 50 && a <= 59) return "age_50_59";
  if (a >= 60 && a <= 69) return "age_60_69";
  if (a >= 70 && a <= 79) return "age_70_79";
  if (a >= 80 && a <= 89) return "age_80_89";
  return null;
}

// kind → { file, mode: "scaled" | "cumPct" | "contrast", column? }
const KIND_MAP = {
  cond1Time:        { file: "d01-cond1-time.parquet",          mode: "scaled" },
  cond2Time:        { file: "d02-cond2-time.parquet",          mode: "scaled" },
  cond3Time:        { file: "d03-cond3-time.parquet",          mode: "scaled" },
  cond4Time:        { file: "d04-cond4-time.parquet",          mode: "scaled" },
  cond1Errors:      { file: "d07-cond1-errors.parquet",        mode: "cumPct" },
  cond2Errors:      { file: "d08-cond2-errors.parquet",        mode: "cumPct" },
  cond3CorrErrors:  { file: "d09-cond3-corr-errors.parquet",   mode: "cumPct" },
  cond3UncorrErrors:{ file: "d10-cond3-uncorr-errors.parquet", mode: "cumPct" },
  cond3TotalScaled: { file: "d11-cond3-total-scaled.parquet",  mode: "scaled" },
  cond4CorrErrors:  { file: "d12-cond4-corr-errors.parquet",   mode: "cumPct" },
  cond4UncorrErrors:{ file: "d13-cond4-uncorr-errors.parquet", mode: "cumPct" },
  cond4TotalScaled: { file: "d14-cond4-total-scaled.parquet",  mode: "scaled" },
  cond31Diff:       { file: "d06-contrast.parquet",            mode: "contrast", column: "inhib_vs_color"  },
  cond43Diff:       { file: "d06-contrast.parquet",            mode: "contrast", column: "inhibsw_vs_inhib" },
};

const MSG = {
  ageOutOfRange: (age, lang) =>
    lang === "de" ? `Alter ${age} außerhalb des Bereichs (8-89)` : `Âge ${age} hors plage (8-89)`,
  invalidValue: (lang) => (lang === "de" ? "Ungültiger Wert" : "Valeur invalide"),
  invalidDiff: (lang) => (lang === "de" ? "Ungültige Differenz" : "Différence invalide"),
  noMatch: (v, age, lang) =>
    lang === "de" ? `Keine Übereinstimmung für ${v}, Alter=${age}` : `Aucune correspondance pour ${v}, âge=${age}`,
  noMatchErrors: (n, age, lang) =>
    lang === "de"
      ? `Keine Übereinstimmung für ${n} Fehler, Alter=${age}`
      : `Aucune correspondance pour ${n} erreurs, âge=${age}`,
  noMatchDelta: (v, lang) =>
    lang === "de" ? `Keine Übereinstimmung für Δ=${v}` : `Aucune correspondance pour Δ=${v}`,
  unknownKind: (kind, lang) => (lang === "de" ? `Unbekannter Typ: ${kind}` : `Type inconnu : ${kind}`),
  valueRequired: (lang) => (lang === "de" ? "Wert erforderlich" : "Valeur requise"),
  ageRequired: (lang) => (lang === "de" ? "Alter erforderlich" : "Âge requis"),
};

/**
 * Look up a D-KEFS scaled score (time → NS, or total errors → NS).
 * Returns the matched scaled_score, or null if no row's cell matches the input.
 */
async function lookupScaled(file, age, raw, lang) {
  const col = getAgeColumn(age);
  if (!col) return { error: MSG.ageOutOfRange(age, lang) };
  const v = parseFloat(raw);
  if (!Number.isFinite(v)) return { error: MSG.invalidValue(lang) };

  const rows = await queryParquet(file);
  const row = rows.find((r) => matchesRange(v, r[col]));
  if (!row) return { error: MSG.noMatch(v, age, lang) };
  return { ns: Number(row.scaled_score) };
}

/**
 * Look up a D-KEFS cumulative-% (≥ raw errors).
 * Rows are keyed by error count (string) including ">=N" caps. Returns the
 * cum% as a number, plus `atCap` when the input fell into the open-ended bin.
 */
async function lookupCumPct(file, age, raw, lang) {
  const col = getAgeColumn(age);
  if (!col) return { error: MSG.ageOutOfRange(age, lang) };
  const n = parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return { error: MSG.invalidValue(lang) };

  const rows = await queryParquet(file);

  // Direct match on the error label.
  const direct = rows.find((r) => String(r.errors) === String(n));
  if (direct && direct[col] !== "" && direct[col] != null) {
    return { pc: Number(direct[col]), atCap: false };
  }

  // Fall back to the cap row (">=N") when present and applicable.
  const capRow = rows.find((r) => /^>=/.test(String(r.errors)));
  if (capRow) {
    const cap = parseInt(String(capRow.errors).replace(/^>=/, ""), 10);
    if (n >= cap && capRow[col] !== "" && capRow[col] != null) {
      return { pc: Number(capRow[col]), atCap: true };
    }
  }

  return { error: MSG.noMatchErrors(n, age, lang) };
}

/**
 * Look up a D-KEFS contrast scaled score (D.6) from a scaled-score difference.
 * Tables don't depend on age — same row set for everyone.
 */
async function lookupContrast(file, column, ssDiff, lang) {
  const v = parseFloat(ssDiff);
  if (!Number.isFinite(v)) return { error: MSG.invalidDiff(lang) };
  const rows = await queryParquet(file);
  const row = rows.find((r) => matchesRange(v, r[column]));
  if (!row) return { error: MSG.noMatchDelta(v, lang) };
  return { ns: Number(row.scaled_score), diff: v };
}

/**
 * Compute a D-KEFS Stroop subscore from a single raw input.
 *
 * @param {Object} args
 * @param {number} args.age   - patient age (8-89), ignored for contrast mode
 * @param {string|number} args.raw
 * @param {string} args.kind  - one of KIND_MAP keys
 * @returns {Promise<{ns:number,diff?:number}|{pc:number,atCap:boolean}|{error:string}>}
 */
export async function calcStroopDkefs({ age, raw, kind, lang }) {
  const cfg = KIND_MAP[kind];
  if (!cfg) return { error: MSG.unknownKind(kind, lang) };
  if (raw === "" || raw == null) return { error: MSG.valueRequired(lang) };

  if (cfg.mode === "contrast") return lookupContrast(cfg.file, cfg.column, raw, lang);
  if (!Number.isFinite(age)) return { error: MSG.ageRequired(lang) };
  if (cfg.mode === "scaled") return lookupScaled(cfg.file, age, raw, lang);
  return lookupCumPct(cfg.file, age, raw, lang);
}

export { KIND_MAP };
